import { z } from 'zod';
import {
  normalizeEncounterTier,
  normalizeRaceId,
  normalizeOptionalBuildTemplateId,
} from '@/IsekaiRPG/modules/character/characterSemantic';

import { RankSchema } from '@/IsekaiRPG/shared/types';
import { CharacterProfileInputSchema } from '@/IsekaiRPG/modules/character/characterTypes';
import { AISkillPayloadSchema } from '@/IsekaiRPG/modules/skill/skillTypes';

const NonEmptyStringSchema = z.string().min(1);

export const SiteEncounterTierSchema = z.preprocess(
  value => normalizeEncounterTier(typeof value === 'string' ? value : undefined),
  z.enum(['杂兵', '精英', '首领']),
);
export type SiteEncounterTier = z.infer<typeof SiteEncounterTierSchema>;

export const SiteEncounterReviewEntrySchema = z.object({
  source: z.enum(['seed', 'unique_character']).default('seed'),
  seedDefinitionId: NonEmptyStringSchema.optional(),
  nameHint: z.string(),
  encounterTier: SiteEncounterTierSchema,
  tags: z.array(z.string()).default([]),
  count: z.number().int().min(1).default(1),
});
export type SiteEncounterReviewEntry = z.infer<typeof SiteEncounterReviewEntrySchema>;

export const SiteEncounterReviewRoomSchema = z.object({
  roomId: NonEmptyStringSchema,
  slot: z.string().min(1),
  title: z.string(),
  description: z.string(),
  tags: z.array(z.string()).default([]),
  isUniqueRunContent: z.boolean().default(false),
  currentEncounters: z.array(SiteEncounterReviewEntrySchema).default([]),
});
export type SiteEncounterReviewRoom = z.infer<typeof SiteEncounterReviewRoomSchema>;

export const SiteEncounterSeedDraftSchema = z.object({
  name: z.string().min(1),
  encounterTier: SiteEncounterTierSchema,
  raceId: z.preprocess(
    value => normalizeRaceId(typeof value === 'string' ? value : undefined),
    NonEmptyStringSchema,
  ),
  buildTemplateId: z.preprocess(
    value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
    NonEmptyStringSchema.optional(),
  ),
  rank: RankSchema.optional(),
  tags: z.array(z.string()).default([]),
});
export type SiteEncounterSeedDraft = z.infer<typeof SiteEncounterSeedDraftSchema>;

export const SiteEncounterSeedCandidateSchema = z.object({
  roomId: NonEmptyStringSchema,
  count: z.number().int().min(1).default(1),
  seedDraft: SiteEncounterSeedDraftSchema,
});
export type SiteEncounterSeedCandidate = z.infer<typeof SiteEncounterSeedCandidateSchema>;

const SiteEncounterAdjustmentEntryCountSchema = z.number().int().min(1).optional();

export const SiteEncounterAdjustmentSeedEntrySchema = z.object({
  kind: z.literal('seed_new'),
  count: SiteEncounterAdjustmentEntryCountSchema,
  seedDraft: SiteEncounterSeedDraftSchema,
});
export type SiteEncounterAdjustmentSeedEntry = z.infer<typeof SiteEncounterAdjustmentSeedEntrySchema>;

export const SiteEncounterAdjustmentUniqueCharacterEntrySchema = z.object({
  kind: z.literal('unique_character'),
  count: SiteEncounterAdjustmentEntryCountSchema,
  encounterTier: SiteEncounterTierSchema,
  profile: CharacterProfileInputSchema,
  skills: z.array(AISkillPayloadSchema).default([]),
});
export type SiteEncounterAdjustmentUniqueCharacterEntry = z.infer<
  typeof SiteEncounterAdjustmentUniqueCharacterEntrySchema
>;

export const SiteEncounterAdjustmentEntrySchema = z.discriminatedUnion('kind', [
  SiteEncounterAdjustmentSeedEntrySchema,
  SiteEncounterAdjustmentUniqueCharacterEntrySchema,
]);
export type SiteEncounterAdjustmentEntry = z.infer<typeof SiteEncounterAdjustmentEntrySchema>;

export const SiteEncounterAdjustmentSchema = z.object({
  roomId: NonEmptyStringSchema,
  mode: z.enum(['append', 'replace_all']),
  entries: z.array(SiteEncounterAdjustmentEntrySchema).default([]),
});
export type SiteEncounterAdjustment = z.infer<typeof SiteEncounterAdjustmentSchema>;
